import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cl from 'classnames';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { EntityId, EntityState } from '@reduxjs/toolkit/src/entities/models';
import { Package, ReportsHeadset } from 'src/models';
import { currencySign, formatDuration } from 'src/helper';
import { useLanguage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import {
  getPackagesAction,
  activateHeadsetSubscriptionAction,
  cancelHeadsetSubscriptionsAction,
} from 'src/services/reportHeadsets';
import { Button, Modal } from 'src/components/general';
import { ReactComponent as PriceSvg } from 'src/icons/price.svg';
import { ReactComponent as DurationSvg } from 'src/icons/duration.svg';
import styles from './serviceHeadsetsStyle.module.scss';

const packagesEntityAdapter = createEntityAdapter<Package>({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  selectId: (pkg: Package) => pkg.id,
});

const packagesSelectors = packagesEntityAdapter.getSelectors();

type SubscriptionsState = Record<
  string,
  EntityState<Package, EntityId> & { isFetching: boolean | null }
>;

const PackageCard: React.FC<{
  package: Package;
  currency: string;
  activate?: () => void;
  disabled?: boolean;
}> = ({
  package: { id, price, name, duration, promo, isKids },
  currency,
  activate,
  disabled = false,
}) => {
  const { t } = useLanguage();

  return (
    <div
      key={`pkg-${id}`}
      className={cl(styles.packageCard, !activate && styles.withoutButton)}
    >
      <div className={styles.topPkgSection}>
        <div className={styles.cardLabel}>
          <span>
            {t('page.reports.headsets.cardLabel', {
              price: `${price} ${currencySign(currency)}`,
            })}
          </span>
          {promo && (
            <div className={cl(styles.tag, styles.blue)}>
              {t('general.promo')}
            </div>
          )}
          {isKids && (
            <div className={cl(styles.tag, styles.green)}>
              {t('general.isKids')}
            </div>
          )}
        </div>
        <div className={styles.cardDescription}>{name}</div>
        <div
          className={cl(styles.cardSpecs, !activate && styles.withoutButton)}
        >
          <div className={styles.cardSpec}>
            <PriceSvg />
            <div className={styles.cardSpecLabel}>
              {price} {currencySign(currency)}
            </div>
          </div>
          <div className={styles.cardSpec}>
            <DurationSvg />
            <div className={styles.cardSpecLabel}>
              {formatDuration(duration)}
            </div>
          </div>
        </div>
      </div>
      {activate && (
        <Button
          label={t('page.reports.headsets.activatePackage')}
          onClick={activate}
          design="lightBlue"
          size="small"
          style={{ width: '100%' }}
          disabled={disabled}
        />
      )}
    </div>
  );
};

type ReportsHeadsetsSubscriptionsParams = {
  subscriptionData: {
    headset: ReportsHeadset;
    activate: boolean;
  } | null;
  close: () => void;
};

export const ServiceHeadsetsSubscriptions = ({
  subscriptionData,
  close,
}: ReportsHeadsetsSubscriptionsParams) => {
  const { t } = useLanguage();
  const dispatch = useAppDispatch();

  const [state, setState] = useState<SubscriptionsState>({});
  const [isLoading, setIsLoading] = useState(false);
  const [activatePkgData, setActivatePkgData] = useState<{
    headset: ReportsHeadset;
    package: Package;
  } | null>(null);

  const companyId = useMemo(
    () => subscriptionData?.headset.companyId ?? null,
    [subscriptionData?.headset.companyId],
  );

  useEffect(() => {
    if (companyId && !state[companyId]) {
      setState((prevState) => ({
        ...prevState,
        [companyId]: packagesEntityAdapter.getInitialState({
          isFetching: null,
        }),
      }));
    }
  }, [companyId, state]);

  useEffect(() => {
    if (!companyId) {
      return;
    }

    const data = state[companyId];

    if (!data || data.isFetching !== null) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      [companyId]: { ...data, isFetching: true },
    }));

    dispatch(getPackagesAction({ companyId })).then((res) => {
      const list = getPackagesAction.fulfilled.match(res) ? res.payload : [];
      packagesEntityAdapter.setAll(data, list);

      setState((prevState) => ({
        ...prevState,
        [companyId]: {
          ...packagesEntityAdapter.setAll(data, list),
          isFetching: false,
        },
      }));
    });
  }, [state, companyId, dispatch]);

  const { isFetching, packages } = useMemo<{
    isFetching: boolean;
    packages: Package[];
  }>(() => {
    if (!companyId) {
      return {
        isFetching: true,
        packages: [],
      };
    }

    const data = state[companyId];

    if (!data) {
      return {
        isFetching: true,
        packages: [],
      };
    }

    return {
      isFetching: data.isFetching ?? true,
      packages: packagesSelectors.selectAll(data).filter((p) => !p.vipAccess),
    };
  }, [companyId, state]);

  const headsetPackageActivationModalTitle = useMemo(() => {
    if (!subscriptionData) {
      return '';
    }

    const { headset } = subscriptionData;

    return t(
      headset.roomLabel
        ? 'page.reports.headsets.packageActivationTitleWithRoom'
        : 'page.reports.headsets.packageActivationTitle',
      {
        label: headset.label || '-',
        roomLabel: headset.roomLabel || '-',
      },
    );
  }, [t, subscriptionData]);

  const activePackage = useMemo(() => {
    if (!subscriptionData || !subscriptionData.headset.activeSubscriptions[0]) {
      return null;
    }

    return packages.find(
      ({ id }) =>
        id === subscriptionData.headset.activeSubscriptions[0].packageId,
    );
  }, [packages, subscriptionData]);

  const activateHeadsetSubscription = useCallback(
    async (headset: ReportsHeadset, packageId: string) => {
      setIsLoading(true);

      const res = await dispatch(
        activateHeadsetSubscriptionAction({
          headsetId: headset.id,
          companyId: headset.companyId,
          data: { packageId },
        }),
      );

      if (activateHeadsetSubscriptionAction.fulfilled.match(res)) {
        close();
      }

      setIsLoading(false);
    },
    [close, dispatch],
  );

  const cancelHeadsetSubscription = useCallback(
    async (headset: ReportsHeadset) => {
      setIsLoading(true);

      const res = await dispatch(
        cancelHeadsetSubscriptionsAction({
          headsetId: headset.id,
          companyId: headset.companyId,
        }),
      );

      if (cancelHeadsetSubscriptionsAction.fulfilled.match(res)) {
        close();
      }

      setIsLoading(false);
    },
    [close, dispatch],
  );

  return (
    <>
      <Modal
        header={headsetPackageActivationModalTitle}
        className={styles.packagesModal}
        show={Boolean(subscriptionData && subscriptionData.activate)}
        close={close}
        footer={
          <Button
            label={t('general.cancel')}
            onClick={close}
            design="outlineBlue"
            autoSize
            className={styles.modalPackageCancelButton}
          />
        }
        closeOnEsc
      >
        <div className={styles.packagesModalContent}>
          {isFetching || !subscriptionData ? (
            <div className={styles.loader} />
          ) : (
            <div className={styles.packageCards}>
              {packages.map((pkg) => (
                <PackageCard
                  key={`pkg-${pkg.id}`}
                  package={pkg}
                  currency={subscriptionData.headset.locationCurrency || ''}
                  activate={() => {
                    setActivatePkgData({
                      headset: subscriptionData.headset,
                      package: pkg,
                    });
                  }}
                  disabled={isLoading}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        show={Boolean(activatePkgData)}
        close={() => {
          setActivatePkgData(null);
        }}
        header={t('page.reports.headsets.activateConfirm', {
          price: `${activatePkgData?.package.price} ${currencySign(subscriptionData?.headset.locationCurrency)}`,
        })}
        className={styles.confirmModal}
        footer={
          <div className={styles.confirmModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={() => {
                setActivatePkgData(null);
              }}
              design="outlineBlue"
              autoSize
              className={cl(styles.modalButton, styles.cancelButton)}
            />
            <Button
              label={t('general.apply')}
              onClick={() => {
                setActivatePkgData(null);
                if (activatePkgData) {
                  activateHeadsetSubscription(
                    activatePkgData.headset,
                    activatePkgData.package.id,
                  );
                }
              }}
              design="lightBlue"
              autoSize
              className={styles.modalButton}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.confirmModalContent}>
          {t('page.reports.headsets.activateConfirm.desc', {
            name: activatePkgData?.package.name,
          })}
        </div>
      </Modal>
      <Modal
        header={t('page.reports.headsets.cancelSubscriptionTitle')}
        className={styles.cancelSubsModal}
        show={Boolean(subscriptionData && !subscriptionData.activate)}
        close={close}
        footer={
          <div className={styles.cancelSubsModalButtons}>
            <Button
              label={t('general.cancel')}
              onClick={close}
              design="outlineBlue"
              autoSize
              className={styles.closeCancelSubsBtn}
            />
            <Button
              label={t('page.reports.headsets.disable')}
              onClick={() => {
                if (subscriptionData) {
                  cancelHeadsetSubscription(subscriptionData.headset);
                }
              }}
              design="pink"
              autoSize
              className={styles.cancelSubsModalBtn}
              disabled={isFetching || isLoading}
            />
          </div>
        }
        closeOnEsc
      >
        <div className={styles.cancelSubsModalContent}>
          {isFetching || !subscriptionData || !activePackage ? (
            <div className={styles.loader} />
          ) : (
            <PackageCard
              package={activePackage}
              currency={subscriptionData.headset.locationCurrency || ''}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default ServiceHeadsetsSubscriptions;
